import { useEffect, useMemo, useState } from 'react';
import { formatRelative, rangeStartMs } from '../lib/time';
import { RankedRow } from './RankedRow';
import { useInsightsData } from '../hooks/useInsightsData';
import type { DateRange, FrictionSubtype, InterruptionSource, ParkingLotEntry, Ping, PingType, RescueKind, RescueSession } from '../types';

type BucketInfo = { dow: number; hour: number; count: number };
type DayLabel = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
type SectionKey = 'snapshot' | 'patterns' | 'helps' | 'signals' | 'parking' | 'export';
type PatternSectionKey = 'calendar' | 'dayBreakdown' | 'hourPattern' | 'sequences';
type CalendarCell = {
  key: string;
  label: number;
  count: number;
  inRange: boolean;
  isToday: boolean;
};
type CalendarMonth = {
  key: string;
  label: string;
  cells: Array<CalendarCell | null>;
};
type DayEvent = {
  id: string;
  label: string;
  startMs: number;
  endMs: number;
  displayStartMs: number;
  displayEndMs: number;
  lane: number;
  laneCount: number;
  kind: 'ping' | 'rescue';
  detail: string;
};

type InsightsTabProps = {
  pings: Ping[];
  rescues: RescueSession[];
  parkingEntries: ParkingLotEntry[];
  insightRange: DateRange;
  onChangeInsightRange: (value: DateRange) => void;
  bucketInfo: BucketInfo | null;
  onSelectBucket: (value: BucketInfo) => void;
  pingTypeLabel: (type: PingType) => string;
  rescueKindLabel: (kind: RescueKind) => string;
  interruptionLabel: (source: InterruptionSource) => string;
  subtypeLabel: (subtype: FrictionSubtype) => string;
  onExportJson: () => void;
  onExportCsv: () => void;
  isPro: boolean;
  onOpenUpgrade: () => void;
  rangeLabel: (range: DateRange) => string;
  dayLabel: (day: DayLabel) => string;
  labels: {
    todaySnapshot: string;
    pings: string;
    recovered: string;
    avgStuck: string;
    patterns: string;
    calendarView: string;
    swipeHint: string;
    topTypes: string;
    topTypesForBucket: string;
    none: string;
    sequences: string;
    medianGap: string;
    whatHelps: string;
    feltHelpful: string;
    outcomesLogged: string;
    smallSample: string;
    started: string;
    completion: string;
    resolved: string;
    medianToNextPing: string;
    associatedHint: string;
    interruptionSources: string;
    noInterruptions: string;
    frictionSubtypes: string;
    noSubtypes: string;
    tinyWins: string;
    captured: string;
    completedRescues: string;
    captureRate: string;
    latest: string;
    noneYet: string;
    prepChecklist: string;
    timedRescues: string;
    phoneSilenced: string;
    tabsClosed: string;
    docOpen: string;
    timerVisible: string;
    frictionStart: string;
    pingsWithSlippingTimer: string;
    medianStuckBeforePing: string;
    meanStuckBeforePing: string;
    parkingLot: string;
    noParkedThoughts: string;
    export: string;
    exportJson: string;
    exportCsv: string;
    empty: string;
    expandAll: string;
    collapseAll: string;
    dayBreakdown: string;
    noDayData: string;
    selectedDay: string;
    rescuesStarted: string;
    topRescues: string;
    hourPattern: string;
    prevMonth: string;
    nextMonth: string;
    eventDetails: string;
    startsAt: string;
    endsAt: string;
    duration: string;
    status: string;
    completed: string;
    canceled: string;
    proLockedTitle: string;
    proLockedBody: string;
    proUpgradeCta: string;
  };
};

const HEATMAP_DAYS: DayLabel[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function InsightsTab({
  pings,
  rescues,
  parkingEntries,
  insightRange,
  onChangeInsightRange,
  bucketInfo,
  onSelectBucket,
  pingTypeLabel,
  rescueKindLabel,
  interruptionLabel,
  subtypeLabel,
  onExportJson,
  onExportCsv,
  isPro,
  onOpenUpgrade,
  rangeLabel,
  dayLabel,
  labels
}: InsightsTabProps) {
  const {
    rangedPings,
    rangedRescues,
    hasInsightsData,
    heat,
    transitions,
    effectiveness,
    interruptions,
    subtypes,
    tinyWins,
    prepSummary,
    frictionStart,
    todayPings,
    todayRescuesCompleted,
    todayAvgStuckMin
  } = useInsightsData({ pings, rescues, insightRange });

  const sortedEffectiveness = useMemo(
    () =>
      [...effectiveness].sort((a, b) => {
        const aScore = a.helpfulRate ?? -1;
        const bScore = b.helpfulRate ?? -1;
        if (bScore !== aScore) return bScore - aScore;
        return b.resolvedRate - a.resolvedRate;
      }),
    [effectiveness]
  );
  const topTypeSummary = heat.topTypes.map((item) => `${pingTypeLabel(item.type)} (${item.count})`).join(', ');
  const quickTopHelps = sortedEffectiveness.filter((item) => item.startedCount > 0).slice(0, 3);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [visibleMonthIndex, setVisibleMonthIndex] = useState(0);
  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>({
    snapshot: false,
    patterns: false,
    helps: true,
    signals: true,
    parking: true,
    export: true
  });
  const [patternCollapsed, setPatternCollapsed] = useState<Record<PatternSectionKey, boolean>>({
    calendar: false,
    dayBreakdown: false,
    hourPattern: false,
    sequences: true
  });

  const toggleSection = (key: SectionKey): void => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setAllSections = (nextCollapsed: boolean): void => {
    setCollapsed({
      snapshot: nextCollapsed,
      patterns: nextCollapsed,
      helps: nextCollapsed,
      signals: nextCollapsed,
      parking: nextCollapsed,
      export: nextCollapsed
    });
  };

  const togglePatternSection = (key: PatternSectionKey): void => {
    setPatternCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const dayCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const ping of rangedPings) {
      const key = dayKeyFromMs(ping.ts_ms);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [rangedPings]);

  const locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

  const calendarMonths = useMemo(() => {
    const startMs = rangeStartMs(insightRange);
    const today = new Date();
    const start = new Date(startMs);
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const months: CalendarMonth[] = [];
    const cursor = new Date(startMonth);
    const todayKey = keyFromDate(today);

    while (cursor <= endMonth) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const firstWeekday = (first.getDay() + 6) % 7;
      const cells: Array<CalendarCell | null> = [];
      for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
      for (let day = 1; day <= last.getDate(); day += 1) {
        const date = new Date(year, month, day);
        const key = keyFromDate(date);
        const inRange = date >= start && date <= today;
        cells.push({
          key,
          label: day,
          count: dayCounts.get(key) ?? 0,
          inRange,
          isToday: key === todayKey
        });
      }
      months.push({
        key: `${year}-${String(month + 1).padStart(2, '0')}`,
        label: first.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
        cells
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months.reverse();
  }, [dayCounts, insightRange, locale, rangedPings.length]);

  useEffect(() => {
    setVisibleMonthIndex(0);
  }, [calendarMonths.length, insightRange]);

  const visibleMonth = calendarMonths[visibleMonthIndex] ?? null;

  useEffect(() => {
    if (calendarMonths.length === 0) {
      setSelectedDayKey(null);
      return;
    }
    if (selectedDayKey && calendarMonths.some((month) => month.cells.some((cell) => cell?.key === selectedDayKey))) return;
    const firstDataDay = calendarMonths
      .flatMap((month) => month.cells)
      .find((cell) => cell && cell.inRange && cell.count > 0);
    const fallbackDay = calendarMonths.flatMap((month) => month.cells).find((cell) => cell && cell.inRange);
    setSelectedDayKey(firstDataDay?.key ?? fallbackDay?.key ?? null);
  }, [calendarMonths, selectedDayKey]);

  const selectedDayPings = useMemo(
    () => (selectedDayKey ? rangedPings.filter((ping) => dayKeyFromMs(ping.ts_ms) === selectedDayKey) : []),
    [rangedPings, selectedDayKey]
  );
  const selectedDayRescues = useMemo(
    () => (selectedDayKey ? rangedRescues.filter((rescue) => dayKeyFromMs(rescue.ts_start_ms) === selectedDayKey) : []),
    [rangedRescues, selectedDayKey]
  );
  const selectedDayTopTypes = useMemo(() => {
    const counts = new Map<PingType, number>();
    for (const ping of selectedDayPings) counts.set(ping.type, (counts.get(ping.type) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([type, count]) => ({ type, count }));
  }, [selectedDayPings]);
  const selectedDayTopRescues = useMemo(() => {
    const counts = new Map<RescueKind, number>();
    for (const rescue of selectedDayRescues) counts.set(rescue.kind, (counts.get(rescue.kind) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([kind, count]) => ({ kind, count }));
  }, [selectedDayRescues]);
  const selectedDayTimeline = useMemo(() => {
    if (!selectedDayKey) return { events: [] as DayEvent[] };
    const dayStart = parseDayKey(selectedDayKey);
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;
    const rawEvents: Array<Omit<DayEvent, 'lane' | 'laneCount' | 'displayStartMs' | 'displayEndMs'>> = [];

    for (const ping of selectedDayPings) {
      const start = Math.max(dayStart, ping.ts_ms);
      const end = Math.min(dayEnd, ping.ts_ms + 10 * 60 * 1000);
      if (end <= start) continue;
      rawEvents.push({
        id: `p-${ping.id}`,
        label: pingTypeLabel(ping.type),
        startMs: start,
        endMs: end,
        kind: 'ping',
        detail: ping.subtype ? subtypeLabel(ping.subtype) : labels.none
      });
    }

    for (const rescue of selectedDayRescues) {
      const fallbackEnd = rescue.ts_start_ms + Math.max(30, rescue.duration_s) * 1000;
      const start = Math.max(dayStart, rescue.ts_start_ms);
      const end = Math.min(dayEnd, rescue.ts_end_ms ?? fallbackEnd);
      if (end <= start) continue;
      rawEvents.push({
        id: `r-${rescue.id}`,
        label: rescueKindLabel(rescue.kind),
        startMs: start,
        endMs: end,
        kind: 'rescue',
        detail: rescue.completed === 1 ? labels.completed : labels.canceled
      });
    }

    rawEvents.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
    const events: DayEvent[] = [];
    const clusters: Array<Array<Omit<DayEvent, 'lane' | 'laneCount' | 'displayStartMs' | 'displayEndMs'>>> = [];
    let currentCluster: Array<Omit<DayEvent, 'lane' | 'laneCount' | 'displayStartMs' | 'displayEndMs'>> = [];
    let clusterEnd = 0;

    for (const event of rawEvents) {
      if (currentCluster.length === 0) {
        currentCluster = [event];
        clusterEnd = event.endMs;
        continue;
      }
      if (event.startMs < clusterEnd) {
        currentCluster.push(event);
        clusterEnd = Math.max(clusterEnd, event.endMs);
        continue;
      }
      clusters.push(currentCluster);
      currentCluster = [event];
      clusterEnd = event.endMs;
    }
    if (currentCluster.length > 0) clusters.push(currentCluster);

    for (const cluster of clusters) {
      const laneEnds: number[] = [];
      const laidOut = cluster.map((event) => {
        let lane = laneEnds.findIndex((endMs) => endMs <= event.startMs);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(event.endMs);
        } else {
          laneEnds[lane] = event.endMs;
        }
        return { ...event, lane };
      });
      const laneCount = Math.max(1, laneEnds.length);
      const laneDisplayEnds = Array.from({ length: laneCount }, () => 0);
      for (const event of laidOut) {
        const minVisualMs = 12 * 60 * 1000;
        const displayStartMs = Math.max(event.startMs, laneDisplayEnds[event.lane]);
        const displayEndMs = Math.max(displayStartMs + minVisualMs, event.endMs);
        laneDisplayEnds[event.lane] = displayEndMs + 60 * 1000;
        events.push({ ...event, laneCount, displayStartMs, displayEndMs });
      }
    }

    return { events };
  }, [labels.canceled, labels.completed, labels.none, pingTypeLabel, rescueKindLabel, selectedDayKey, selectedDayPings, selectedDayRescues, subtypeLabel]);

  useEffect(() => {
    if (!selectedDayKey) {
      setSelectedEventId(null);
      return;
    }
    if (!selectedEventId) return;
    if (selectedDayTimeline.events.some((event) => event.id === selectedEventId)) return;
    setSelectedEventId(null);
  }, [selectedDayKey, selectedDayTimeline.events, selectedEventId]);

  return (
    <section>
      <div className="history-controls">
        <div className="filters">
          {(['7d', '30d', '90d', 'all'] as DateRange[]).map((r) => (
            <button key={r} className={insightRange === r ? 'active' : ''} onClick={() => onChangeInsightRange(r)}>
              {rangeLabel(r)}
            </button>
          ))}
        </div>
      </div>

      {!hasInsightsData && <div className="card empty-state-card insights-empty-card">{labels.empty}</div>}
      {hasInsightsData && (
        <div className="insights-flow">
          <div className="card insights-hero">
            <div className="insights-hero-kicker">Field Notes</div>
            <div className="insights-hero-title">{labels.patterns}</div>
            <div className="insights-hero-body">{topTypeSummary || labels.none}</div>
          </div>

          <div className="timer-buttons insights-global-actions">
            <button onClick={() => setAllSections(false)}>{labels.expandAll}</button>
            <button onClick={() => setAllSections(true)}>{labels.collapseAll}</button>
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('snapshot')}>
              <span>{collapsed.snapshot ? '▸' : '▾'}</span>
              <span>{labels.todaySnapshot}</span>
            </button>
            {!collapsed.snapshot && (
              <div className="insights-panel-body">
                <div className="stack metrics3">
                  <div className="insights-metric-tile">
                    <div className="row-title">{labels.pings}</div>
                    <div>{todayPings.length}</div>
                  </div>
                  <div className="insights-metric-tile">
                    <div className="row-title">{labels.recovered}</div>
                    <div>{todayRescuesCompleted}</div>
                  </div>
                  <div className="insights-metric-tile">
                    <div className="row-title">{labels.avgStuck}</div>
                    <div>{todayAvgStuckMin === null ? labels.none : `${todayAvgStuckMin.toFixed(1)}m`}</div>
                  </div>
                </div>
                <div className="insights-stat-block">
                  <div className="row-title insights-stat-title">{labels.topTypes}</div>
                  <div>{topTypeSummary || labels.none}</div>
                </div>
                <div className="insights-stat-block">
                  <div className="row-title insights-stat-title">{labels.whatHelps}</div>
                  {quickTopHelps.length === 0 && <div className="small">{labels.none}</div>}
                  {quickTopHelps.map((item) => (
                    <div key={`quick-help-${item.kind}`} className="insights-inline-row">
                      <span>{rescueKindLabel(item.kind)}</span>
                      <span>
                        {item.helpfulRate === null
                          ? `${labels.resolved}: ${(item.resolvedRate * 100).toFixed(0)}%`
                          : `${labels.feltHelpful}: ${(item.helpfulRate * 100).toFixed(0)}%`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('patterns')}>
              <span>{collapsed.patterns ? '▸' : '▾'}</span>
              <span>{labels.patterns}</span>
            </button>
            {!collapsed.patterns && (
              <div className="insights-panel-body">
                <div className="insights-subtree">
                  <button className="insights-subtree-toggle" onClick={() => togglePatternSection('calendar')}>
                    <span>{patternCollapsed.calendar ? '▸' : '▾'}</span>
                    <span>{labels.calendarView}</span>
                  </button>
                  {!patternCollapsed.calendar && visibleMonth && (
                    <div className="insights-subtree-body">
                      <div
                        className="card calendar-month-card"
                        onTouchStart={(event) => {
                          const touch = event.touches[0];
                          if (!touch) return;
                          (event.currentTarget as HTMLDivElement).dataset.touchX = String(touch.clientX);
                        }}
                        onTouchEnd={(event) => {
                          const touch = event.changedTouches[0];
                          const startX = Number((event.currentTarget as HTMLDivElement).dataset.touchX ?? '0');
                          if (!touch || !Number.isFinite(startX)) return;
                          const delta = touch.clientX - startX;
                          if (delta <= -40 && visibleMonthIndex < calendarMonths.length - 1) setVisibleMonthIndex((prev) => prev + 1);
                          if (delta >= 40 && visibleMonthIndex > 0) setVisibleMonthIndex((prev) => prev - 1);
                        }}
                      >
                        <div className="calendar-month-nav">
                          <button
                            disabled={visibleMonthIndex >= calendarMonths.length - 1}
                            onClick={() => setVisibleMonthIndex((prev) => Math.min(calendarMonths.length - 1, prev + 1))}
                            title={labels.prevMonth}
                          >
                            ‹
                          </button>
                          <div className="row-title calendar-month-title">{visibleMonth.label}</div>
                          <button
                            disabled={visibleMonthIndex <= 0}
                            onClick={() => setVisibleMonthIndex((prev) => Math.max(0, prev - 1))}
                            title={labels.nextMonth}
                          >
                            ›
                          </button>
                        </div>
                        <div className="calendar-dow-row">
                          {HEATMAP_DAYS.map((day) => (
                            <div key={`dow-${visibleMonth.key}-${day}`} className="calendar-dow">
                              {dayLabel(day)}
                            </div>
                          ))}
                        </div>
                        <div className="calendar-grid">
                          {visibleMonth.cells.map((cell, index) => {
                            if (!cell) return <div key={`empty-${visibleMonth.key}-${index}`} className="calendar-cell-empty" />;
                            return (
                              <button
                                key={cell.key}
                                className={`calendar-cell${selectedDayKey === cell.key ? ' is-selected' : ''}${cell.isToday ? ' is-today' : ''}`}
                                disabled={!cell.inRange}
                                onClick={() => {
                                  setSelectedDayKey(cell.key);
                                  onSelectBucket({ dow: 0, hour: 0, count: cell.count });
                                }}
                                style={{ opacity: cell.count === 0 ? 0.32 : Math.min(1, 0.34 + cell.count / 6) }}
                                title={`${cell.key}: ${cell.count} ${labels.pings.toLowerCase()}`}
                              >
                                <span className="calendar-day">{cell.label}</span>
                                <span className="calendar-count">{cell.count}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="insights-subtree">
                  <button className="insights-subtree-toggle" onClick={() => togglePatternSection('dayBreakdown')}>
                    <span>{patternCollapsed.dayBreakdown ? '▸' : '▾'}</span>
                    <span>{labels.dayBreakdown}</span>
                  </button>
                  {!patternCollapsed.dayBreakdown && (
                    <div className="insights-subtree-body">
                      {selectedDayKey && (
                        <div className="insights-stat-block insights-day-card">
                          <div className="insights-day-kicker">{labels.selectedDay}</div>
                          <div className="insights-day-title">{selectedDayKey}</div>
                          <div className="insights-day-grid">
                            <div className="insights-day-metric">
                              <span className="insights-day-label">{labels.pings}</span>
                              <span>{selectedDayPings.length}</span>
                            </div>
                            <div className="insights-day-metric">
                              <span className="insights-day-label">{labels.rescuesStarted}</span>
                              <span>{selectedDayRescues.length}</span>
                            </div>
                            <div className="insights-day-metric">
                              <span className="insights-day-label">{labels.recovered}</span>
                              <span>{selectedDayRescues.filter((item) => item.completed === 1).length}</span>
                            </div>
                          </div>
                          <div className="insights-day-copy">
                            <div>{labels.topTypes}: {selectedDayTopTypes.map((item) => `${pingTypeLabel(item.type)} (${item.count})`).join(', ') || labels.none}</div>
                            <div>{labels.topRescues}: {selectedDayTopRescues.map((item) => `${rescueKindLabel(item.kind)} (${item.count})`).join(', ') || labels.none}</div>
                          </div>
                        </div>
                      )}
                      {!selectedDayKey && <div className="small">{labels.noDayData}</div>}
                    </div>
                  )}
                </div>

                <div className="insights-subtree">
                  <button className="insights-subtree-toggle" onClick={() => togglePatternSection('hourPattern')}>
                    <span>{patternCollapsed.hourPattern ? '▸' : '▾'}</span>
                    <span>{labels.hourPattern}</span>
                  </button>
                  {!patternCollapsed.hourPattern && (
                    <div className="insights-subtree-body">
                      {selectedDayKey && selectedDayTimeline.events.length > 0 && (
                        <div className="calendar-day-timeline">
                          <div className="calendar-time-column">
                            {Array.from({ length: 24 }).map((_, hour) => (
                              <div key={`tick-${hour}`} className="calendar-time-tick">
                                {`${String(hour).padStart(2, '0')}:00`}
                              </div>
                            ))}
                          </div>
                          <div className="calendar-events-grid">
                            {Array.from({ length: 24 }).map((_, hour) => (
                              <div key={`line-${hour}`} className="calendar-hour-line" />
                            ))}
                            {selectedDayTimeline.events.map((event) => {
                              const dayStart = parseDayKey(selectedDayKey);
                              const startMin = (event.displayStartMs - dayStart) / 60000;
                              const durationMin = Math.max(12, (event.displayEndMs - event.displayStartMs) / 60000);
                              const laneWidth = 100 / event.laneCount;
                              const left = event.lane * laneWidth;
                              return (
                                <div
                                  key={event.id}
                                  className={`calendar-event ${event.kind === 'rescue' ? 'is-rescue' : 'is-ping'}`}
                                  style={{
                                    top: `${(startMin / 60) * 48}px`,
                                    height: `${Math.max(18, (durationMin / 60) * 48)}px`,
                                    left: `calc(${left}% + 2px)`,
                                    width: `calc(${laneWidth}% - 4px)`
                                  }}
                                  title={`${formatClock(event.startMs)} - ${formatClock(event.endMs)}`}
                                  onClick={() => setSelectedEventId(event.id)}
                                >
                                  <div className="calendar-event-title">{event.label}</div>
                                  <div className="calendar-event-time">{`${formatClock(event.startMs)} - ${formatClock(event.endMs)}`}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {selectedDayKey && selectedDayTimeline.events.length === 0 && <div className="small">{labels.noDayData}</div>}
                      {!selectedDayKey && <div className="small">{labels.noDayData}</div>}
                      {selectedEventId && (
                        <div className="insights-stat-block insights-event-card">
                          {(() => {
                            const selected = selectedDayTimeline.events.find((item) => item.id === selectedEventId);
                            if (!selected) return <div className="small">{labels.noDayData}</div>;
                            return (
                              <div className="stack">
                                <div className="insights-day-kicker">{labels.eventDetails}</div>
                                <div className="insights-day-title">{selected.label}</div>
                                <div className="insights-day-copy">{selected.detail}</div>
                                <div className="insights-event-facts">
                                  <div className="insights-event-fact"><span>{labels.startsAt}</span><strong>{formatClock(selected.startMs)}</strong></div>
                                  <div className="insights-event-fact"><span>{labels.endsAt}</span><strong>{formatClock(selected.endMs)}</strong></div>
                                  <div className="insights-event-fact"><span>{labels.duration}</span><strong>{formatDurationMinutes(selected.endMs - selected.startMs)}</strong></div>
                                  <div className="insights-event-fact"><span>{labels.status}</span><strong>{selected.kind === 'rescue' ? selected.detail : labels.none}</strong></div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="insights-subtree">
                  <button className="insights-subtree-toggle" onClick={() => togglePatternSection('sequences')}>
                    <span>{patternCollapsed.sequences ? '▸' : '▾'}</span>
                    <span>{labels.sequences}</span>
                  </button>
                  {!patternCollapsed.sequences && (
                    <div className="insights-subtree-body">
                      {isPro ? (
                        <div className="stack insights-sequence-list">
                          {transitions.top5.map((t) => (
                            <div key={`${t.from}-${t.to}`} className="insights-stat-block insights-sequence-card">
                              <div className="insights-day-kicker">{labels.sequences}</div>
                              <div className="insights-sequence-title">{`${pingTypeLabel(t.from)} -> ${pingTypeLabel(t.to)}`}</div>
                              <div className="insights-day-copy">{`${t.count} · ${labels.medianGap} ${Math.round(t.medianGapMin)}m`}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="card">
                          <div className="row-title">{labels.proLockedTitle}</div>
                          <div className="small">{labels.proLockedBody}</div>
                          <button className="settings-action" onClick={onOpenUpgrade}>
                            {labels.proUpgradeCta}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('helps')}>
              <span>{collapsed.helps ? '▸' : '▾'}</span>
              <span>{labels.whatHelps}</span>
            </button>
            {!collapsed.helps && (
              <div className="insights-panel-body">
                {isPro ? (
                  <>
                    <div className="stack">
                      {sortedEffectiveness.map((e) => (
                        <div key={e.kind} className="insights-stat-block insights-help-card">
                          <div className="insights-sequence-title">
                            {rescueKindLabel(e.kind)} {e.smallSample ? `(${labels.smallSample})` : ''}
                          </div>
                          <div className="insights-event-facts">
                            <div className="insights-event-fact"><span>{labels.started}</span><strong>{e.startedCount}</strong></div>
                            <div className="insights-event-fact"><span>{labels.completion}</span><strong>{(e.completionRate * 100).toFixed(0)}%</strong></div>
                            <div className="insights-event-fact"><span>{labels.resolved}</span><strong>{(e.resolvedRate * 100).toFixed(0)}%</strong></div>
                            <div className="insights-event-fact"><span>{labels.feltHelpful}</span><strong>{e.helpfulRate === null ? labels.none : `${(e.helpfulRate * 100).toFixed(0)}%`}</strong></div>
                            <div className="insights-event-fact"><span>{labels.outcomesLogged}</span><strong>{e.outcomeLoggedCount}</strong></div>
                            <div className="insights-event-fact"><span>{labels.medianToNextPing}</span><strong>{e.medianTimeToNextPingMin === null ? labels.none : `${Math.round(e.medianTimeToNextPingMin)}m`}</strong></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="small">{labels.associatedHint}</div>
                    <div className="insights-split">
                      <div className="insights-stat-block">
                        <div className="row-title insights-stat-title">{labels.interruptionSources}</div>
                        <div className="stack insights-ranked-list">
                          {interruptions.map((i) => (
                            <RankedRow key={i.source} label={interruptionLabel(i.source)} value={i.count} max={interruptions[0]?.count ?? 1} />
                          ))}
                          {interruptions.length === 0 && <div className="small insights-empty-block">{labels.noInterruptions}</div>}
                        </div>
                      </div>
                      <div className="insights-stat-block">
                        <div className="row-title insights-stat-title">{labels.frictionSubtypes}</div>
                        <div className="stack insights-ranked-list">
                          {subtypes.map((s) => (
                            <RankedRow key={s.subtype} label={subtypeLabel(s.subtype)} value={s.count} max={subtypes[0]?.count ?? 1} />
                          ))}
                          {subtypes.length === 0 && <div className="small insights-empty-block">{labels.noSubtypes}</div>}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="card">
                    <div className="row-title">{labels.proLockedTitle}</div>
                    <div className="small">{labels.proLockedBody}</div>
                    <button className="settings-action" onClick={onOpenUpgrade}>
                      {labels.proUpgradeCta}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('signals')}>
              <span>{collapsed.signals ? '▸' : '▾'}</span>
              <span>{labels.tinyWins}</span>
            </button>
            {!collapsed.signals && (
              <div className="insights-panel-body">
                {isPro ? (
                  <>
                    <div className="insights-stat-block insights-day-card">
                      <div className="insights-day-kicker">{labels.tinyWins}</div>
                      <div className="insights-day-grid">
                        <div className="insights-day-metric">
                          <span className="insights-day-label">{labels.captured}</span>
                          <span>{tinyWins.withTinyWin}</span>
                        </div>
                        <div className="insights-day-metric">
                          <span className="insights-day-label">{labels.completedRescues}</span>
                          <span>{tinyWins.completedRescues}</span>
                        </div>
                        <div className="insights-day-metric">
                          <span className="insights-day-label">{labels.captureRate}</span>
                          <span>{(tinyWins.captureRate * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="small insights-day-copy">{labels.latest}: {tinyWins.latest.join(' | ') || labels.noneYet}</div>
                    </div>

                    <div className="insights-split">
                      <div className="insights-stat-block">
                        <div className="row-title insights-stat-title">{labels.prepChecklist}</div>
                        <div className="insights-inline-list">
                          <div className="insights-inline-row"><span>{labels.timedRescues}</span><strong>{prepSummary.timedRescues}</strong></div>
                          <div className="insights-inline-row"><span>{labels.phoneSilenced}</span><strong>{(prepSummary.phoneSilencedRate * 100).toFixed(0)}%</strong></div>
                          <div className="insights-inline-row"><span>{labels.tabsClosed}</span><strong>{(prepSummary.tabsClosedRate * 100).toFixed(0)}%</strong></div>
                          <div className="insights-inline-row"><span>{labels.docOpen}</span><strong>{(prepSummary.docOpenRate * 100).toFixed(0)}%</strong></div>
                          <div className="insights-inline-row"><span>{labels.timerVisible}</span><strong>{(prepSummary.timerVisibleRate * 100).toFixed(0)}%</strong></div>
                        </div>
                      </div>

                      <div className="insights-stat-block">
                        <div className="row-title insights-stat-title">{labels.frictionStart}</div>
                        <div className="insights-inline-list">
                          <div className="insights-inline-row"><span>{labels.pingsWithSlippingTimer}</span><strong>{frictionStart.withStartCount}</strong></div>
                          <div className="insights-inline-row"><span>{labels.medianStuckBeforePing}</span><strong>{frictionStart.medianLeadMin === null ? labels.none : `${frictionStart.medianLeadMin.toFixed(1)}m`}</strong></div>
                          <div className="insights-inline-row"><span>{labels.meanStuckBeforePing}</span><strong>{frictionStart.meanLeadMin === null ? labels.none : `${frictionStart.meanLeadMin.toFixed(1)}m`}</strong></div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="card">
                    <div className="row-title">{labels.proLockedTitle}</div>
                    <div className="small">{labels.proLockedBody}</div>
                    <button className="settings-action" onClick={onOpenUpgrade}>
                      {labels.proUpgradeCta}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('parking')}>
              <span>{collapsed.parking ? '▸' : '▾'}</span>
              <span>{labels.parkingLot}</span>
            </button>
            {!collapsed.parking && (
              <div className="insights-panel-body">
                <div className="stack insights-parking-list">
                  {parkingEntries.slice(0, 5).map((entry) => (
                    <div key={entry.id} className="insights-parking-entry">
                      <div>{entry.text}</div>
                      <div className="small insights-parking-time">{formatRelative(entry.ts_ms)}</div>
                    </div>
                  ))}
                  {parkingEntries.length === 0 && <div className="small insights-empty-block">{labels.noParkedThoughts}</div>}
                </div>
              </div>
            )}
          </div>

          <div className="insights-panel">
            <button className="insights-panel-toggle" onClick={() => toggleSection('export')}>
              <span>{collapsed.export ? '▸' : '▾'}</span>
              <span>{labels.export}</span>
            </button>
            {!collapsed.export && (
              <div className="insights-panel-body">
                {isPro ? (
                  <div className="timer-buttons">
                    <button onClick={onExportJson}>{labels.exportJson}</button>
                    <button onClick={onExportCsv}>{labels.exportCsv}</button>
                  </div>
                ) : (
                  <div className="card">
                    <div className="row-title">{labels.proLockedTitle}</div>
                    <div className="small">{labels.proLockedBody}</div>
                    <button className="settings-action" onClick={onOpenUpgrade}>
                      {labels.proUpgradeCta}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function keyFromDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function dayKeyFromMs(tsMs: number): string {
  return keyFromDate(new Date(tsMs));
}

function parseDayKey(dayKey: string): number {
  const [y, m, d] = dayKey.split('-').map((item) => Number(item));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return Date.now();
  return new Date(y, m - 1, d).getTime();
}

function formatClock(tsMs: number): string {
  const date = new Date(tsMs);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatDurationMinutes(durationMs: number): string {
  const minutes = Math.max(1, Math.round(durationMs / 60000));
  return `${minutes}m`;
}
